import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { CustomFormValidators } from "@app/directives/_forms/form-validation.service";
import { IVorRecord } from "@app/main/components/resourceManagement/vor/vor.interfaces";
import { VorService } from "@app/services/vor.service";
import { CalendarDirection } from "@app/enum/CalendarDirection.enum";
import "./vor-editor.component.scss";

@Component({
    selector: 'vor-editor',
    template: require("./vor-editor.component.html")
})
export class VorEditorComponent implements OnInit {

    @Input() vehicleId: number;
    @Input() record: IVorRecord;
    @Input() allRecords: IVorRecord[];
    @Output() onSaved = new EventEmitter<IVorRecord>();

    // --

    saving = false;

    startDate = new Date();
    startTime: string;
    endDate = new Date();
    endTime: string;

    form: FormGroup;

    isEditing: boolean;
    hideEndDate: boolean = true;
    isOverlapping: boolean = false;

    CalendarDirection = CalendarDirection;

    constructor(
        private readonly vorService: VorService) {
    }

    ngOnInit(): void {
        if (this.record != undefined) {
            this.isEditing = true;
            this.hideEndDate = this.record.EndDate == null;
        }

        this.initializeDates();
        this.createFormGroup();
    }

    ngOnChanges(simpleChanges: SimpleChanges): void {
        this.form.controls["description"].setValue(this.record?.Description);
        this.form.controls["hideEndDate"].setValue(this.hideEndDate);

        if (this.hideEndDate) {
            this.setDefaultEndTime();
        }
    }

    onStartTimeChange(time: string): void {
        this.startTime = time;
        this.setTime(this.startDate, time);
    }

    onEndTimeChange(time: string): void {
        this.endTime = time;
        this.setTime(this.endDate, time);
    }

    onStartDateChanged(date: Date): void {
        this.startDate = new Date(date);
        this.onStartTimeChange(this.startTime);

        if (!this.hideEndDate && this.startDate > this.endDate) {
            this.onEndDateChanged(this.startDate);
        }

        this.isOverlapping = this.isRecordOverlapping();
    }

    onEndDateChanged(date: Date): void {
        this.endDate = new Date(date);

        this.onEndTimeChange(this.endTime);

        this.isOverlapping = this.isRecordOverlapping();
    }

    save(): void {
        const record: IVorRecord = {
            Id: this.record?.Id || 0,
            Description: this.form.controls["description"].value,
            StartDate: this.startDate,
            EndDate: this.hideEndDate ? null : this.endDate,
            VehicleId: this.record?.VehicleId || this.vehicleId
        };

        this.saving = true;
        this.vorService.saveRecord(record).then(updatedRecord => {
            this.saving = false;

            this.onSaved.emit(updatedRecord);
        });
    }

    onChecked(checked: boolean): void {
        this.hideEndDate = checked;
        this.isOverlapping = this.isRecordOverlapping();

        if (!this.hideEndDate) {
            this.setDefaultEndTime();
        }
    }

    // ---

    private createFormGroup(): void {
        if (this.form) {
            return;
        }

        this.form = new FormGroup({
            "description": new FormControl(this.record?.Description, [Validators.required, CustomFormValidators.emptyWhiteSpace]),
            "hideEndDate": new FormControl(this.hideEndDate)
        });

    }

    private setTime(date: Date, time: string): void {
        const [hours, minutes] = time.split(':').map(Number);
        date.setHours(hours, minutes, 0);     
        this.isOverlapping = this.isRecordOverlapping();   
    }

    private getRoundedTimeString(date: Date): string {
        const minutes = date.getMinutes();
        const roundedMinutes = Math.round(minutes / 15) * 15;
        const roundedDate = new Date(date);

        if (roundedMinutes === 60) {
            roundedDate.setHours(roundedDate.getHours() + 1);
            roundedDate.setMinutes(0);
        } else {
            roundedDate.setMinutes(roundedMinutes);
        }

        return roundedDate.toTimeString().slice(0, 5); // Returns "HH:mm"
    }

    private setDefaultEndTime(): void {
        if (this.endTime) {
            return;
        }

        this.endTime = this.getRoundedTimeString(this.startDate);
        this.onEndTimeChange(this.endTime);
    }

    private initializeDates(): void {
        if (this.record) {
            this.startDate = this.record.StartDate;
            this.startTime = this.getRoundedTimeString(this.startDate);
            this.onStartTimeChange(this.startTime);

            if (this.record.EndDate) {
                this.endDate = this.record.EndDate;
                this.endTime = this.getRoundedTimeString(this.endDate);
                this.onEndTimeChange(this.endTime);
            }
        } else {
            this.startTime = this.getRoundedTimeString(new Date());
            this.onStartTimeChange(this.startTime);
        }
    }

    private isRecordOverlapping(): boolean {        
        const record: IVorRecord = {
            Id: this.record?.Id || 0,
            Description: "",
            StartDate: this.startDate,
            EndDate: this.hideEndDate ? null : this.endDate,
            VehicleId: 0
        };

        return this.vorService.checkForOverlappingVorRecords(record, this.allRecords);
    }
}
